/**
 * AppSidebar — context-aware navigation.
 *
 * Two sections:
 *   - Property — visible only when on /p/:propertyId/<layer> routes.
 *     Lists every data layer for the selected property (Overview,
 *     Crawl, Pages, Ahrefs, GSC, GA4, Server logs, Insights, Audits,
 *     Reports). Each link navigates to /p/<id>/<layer>.
 *   - Global — always visible. Cross-property views and admin
 *     surfaces (Dashboard, All properties, Capabilities, Inspector,
 *     Settings).
 *
 * The mental model: pick a property in the header switcher, then use
 * the Property section to drill through its data layers. Global section
 * is for system-wide stuff that isn't tied to a single property.
 */
import { NavLink, useMatch } from "react-router-dom";
import {
  LayoutDashboard, Globe, ScrollText, AlertCircle,
  FileText, Settings as SettingsIcon, Database, Layers,
  Home, Search, BookOpen, Activity, Server, BarChart3,
  Link as LinkIcon, Tag, Sparkles, MessageSquare,
  Sliders, Gauge,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { NwsLogo } from "@/components/NwsLogo";
import { useProperty } from "@/lib/queries";


// Property data layers — order matters (most-used first)
const PROPERTY_NAV = [
  { tail: "overview",     label: "Overview",        icon: Home },
  { tail: "crawl",        label: "Crawl",           icon: Search },
  { tail: "pages",        label: "Pages",           icon: BookOpen },
  { tail: "ahrefs",       label: "Ahrefs",          icon: BarChart3 },
  { tail: "dataforseo",   label: "DataForSEO",      icon: Database },
  { tail: "gsc",          label: "Search Console",  icon: Globe },
  { tail: "ga4",          label: "Analytics",       icon: Activity },
  { tail: "backlinks",    label: "Backlinks",       icon: LinkIcon },
  { tail: "keywords",     label: "Keywords",        icon: Tag },
  { tail: "server-logs",  label: "Server logs",     icon: Server },
  { tail: "reviews",      label: "Reviews",         icon: MessageSquare },
  { tail: "cwv",          label: "Core Web Vitals", icon: Gauge },
  { tail: "insights",     label: "Insights",        icon: Sparkles },
  { tail: "audits",       label: "Audits",          icon: ScrollText },
  { tail: "reports",      label: "Reports",         icon: FileText },
  { tail: "settings",     label: "Settings",        icon: Sliders },
] as const;


// Global cross-property views — always visible
const GLOBAL_NAV = [
  { to: "/dashboard",     label: "Dashboard",     icon: LayoutDashboard },
  { to: "/properties",    label: "All properties", icon: Globe },
  { to: "/audits",        label: "All audits",    icon: ScrollText },
  { to: "/findings",      label: "All findings",  icon: AlertCircle },
  { to: "/reports",       label: "All reports",   icon: FileText },
  { to: "/capabilities",  label: "Capabilities",  icon: Layers },
  { to: "/inspector",     label: "Inspector",     icon: Database },
  { to: "/settings",      label: "Settings",      icon: SettingsIcon },
] as const;


export default function AppSidebar() {
  const propMatch = useMatch("/p/:propertyId/*");
  const currentPropertyId = propMatch ? Number(propMatch.params.propertyId) : null;

  return (
    <aside className="hidden w-60 shrink-0 border-r border-border bg-card md:flex md:flex-col">
      <div className="flex h-16 items-center gap-3 border-b border-border bg-gradient-to-br from-primary/10 via-primary/5 to-transparent px-5">
        <NwsLogo className="h-7 w-7 text-primary" />
        <div className="leading-tight">
          <div className="text-sm font-semibold tracking-tight">Auriel</div>
          <div className="whitespace-nowrap text-[10px] uppercase tracking-tight text-muted-foreground">
            Web Intelligence
          </div>
        </div>
      </div>

      <nav className="flex-1 space-y-4 overflow-y-auto px-3 py-4">
        {currentPropertyId && (
          <PropertySection propertyId={currentPropertyId} />
        )}

        <Section
          label={currentPropertyId ? "Global" : ""}
          showLabel={currentPropertyId !== null}
        >
          {GLOBAL_NAV.map((item) => (
            <NavItem key={item.to} to={item.to} label={item.label} Icon={item.icon} />
          ))}
        </Section>
      </nav>

      <div className="border-t border-border px-5 py-4 text-xs text-muted-foreground">
        <div className="font-medium text-foreground">v0.7.0</div>
        <div>nws® web intelligence</div>
      </div>
    </aside>
  );
}


function PropertySection({ propertyId }: { propertyId: number }) {
  const property = useProperty(propertyId);
  const domain = property.data?.domain;

  return (
    <Section label={domain ?? `Property #${propertyId}`} showLabel mono>
      {PROPERTY_NAV.map((item) => (
        <NavItem
          key={item.tail}
          to={`/p/${propertyId}/${item.tail}`}
          label={item.label}
          Icon={item.icon}
        />
      ))}
    </Section>
  );
}


function Section({ label, showLabel, mono, children }: {
  label: string;
  showLabel: boolean;
  mono?: boolean;
  children: React.ReactNode;
}) {
  return (
    <div className="space-y-1">
      {showLabel && (
        <div className={cn(
          "px-3 py-1 text-[10px] uppercase tracking-wider text-muted-foreground",
          mono && "font-mono normal-case tracking-normal text-[11px]",
        )}>
          {label}
        </div>
      )}
      {children}
    </div>
  );
}


function NavItem({ to, label, Icon }: {
  to: string;
  label: string;
  Icon: typeof Globe;
}) {
  return (
    <NavLink
      to={to}
      end={false}
      className={({ isActive }) =>
        cn(
          "flex items-center gap-3 rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
          isActive
            ? "bg-primary/10 text-primary"
            : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
        )
      }
    >
      <Icon className="h-4 w-4" />
      {label}
    </NavLink>
  );
}
